/**
 * 按 Section.type 分组 + 区间顺序排序（F03，Q6 决策）。
 *
 * `store.sections` 已按 Section.sort 全局升序。HomePage 渲染时不能逐项
 * 遍历——`[problem(10), method(20), problem(30)]` 会渲染成
 * `problem→method→problem` 交错形态。正确口径是把同 type 聚为一个
 * "区"，区间顺序 = 各区内最小 sort 升序，区内顺序 = sort 升序；单实例
 * Section（intro / video / banner）走同规则即 N=1 的"区"。
 *
 * 纯函数 + readonly 入参 + readonly 返回，无 Vue API 依赖，便于单测。
 */
import type { PublicSectionView } from '@/api/types';

export interface SectionGroup {
  readonly type: string;
  readonly minSort: number;
  readonly sections: readonly PublicSectionView[];
}

/**
 * 按 type 聚区 → 各区按 min(sort) 升序 → 区内按 sort 升序。
 * 同 sort 时按 id 字典序兜底，避免输入顺序敏感导致不稳定排序。
 */
export function groupSectionsByType(
  sections: readonly PublicSectionView[],
): readonly SectionGroup[] {
  const buckets = new Map<string, PublicSectionView[]>();
  for (const section of sections) {
    const bucket = buckets.get(section.type);
    if (bucket === undefined) {
      buckets.set(section.type, [section]);
    } else {
      bucket.push(section);
    }
  }
  const groups: SectionGroup[] = [];
  for (const [type, items] of buckets) {
    items.sort((a, b) => a.sort - b.sort || a.id.localeCompare(b.id));
    const first = items[0];
    if (first === undefined) {
      throw new Error(`[groupSectionsByType] empty bucket for type "${type}"`);
    }
    groups.push({ type, minSort: first.sort, sections: items });
  }
  groups.sort((a, b) => a.minSort - b.minSort || a.type.localeCompare(b.type));
  return groups;
}
